import {
  Box,
  Button,
  Collapse,
  Flex,
  StyleProps,
  VStack,
} from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Transition } from 'react-transition-group'
import SimpleBar from 'simplebar-react'

import { Role } from '../../config/roles'
import { useAppStore } from '../../store/appStore/appStore'
import { usePersisted } from '../../store/appStore/persistence/usePersistence'
import { Icon } from '../Icons/Icon'
import { Logo } from '../Logo/Logo'
import { ExpandButton } from './ExpandButton'
import { HomeButton } from './HomeButton'
import { MenuSubheader, SidenavButton, SidenavTooltip } from './Sidenav.parts'
import {
  childTransition,
  cubicBezierFunction,
  defaultStyle,
  sideNavTransition,
} from './Sidenav.utils'

export interface SideNavProps extends StyleProps {}

export const Sidenav = (props: SideNavProps) => {
  const { hasRole, sidebarConfigs } = useAppStore(state => ({
    hasRole: state.auth.hasRole,
    sidebarConfigs: state.cms.sidebarConfigs,
  }))

  const [renderDelay, setRenderDelay] = useState(true)
  const [isOpen, setIsOpen] = usePersisted({
    key: 'SidebarIsOpen',
    value: true,
    where: 'localstorage',
    until: 'forever',
  })

  const menus = sidebarConfigs

  const [isExpanded, setIsExpanded] = useState(menus.map(() => true))

  useEffect(() => {
    const timerId = setTimeout(
      () => {
        setRenderDelay(isOpen!)
      },
      isOpen ? 250 : 0,
    )
    return () => clearTimeout(timerId)
  }, [isOpen])

  const toggleExpand = () => {
    setIsOpen(!isOpen)
  }

  const { pathname } = useLocation()

  const mainRef = useRef<HTMLDivElement>(null)
  const subRef = useRef<HTMLDivElement>(null)

  return (
    <>
      <Transition
        nodeRef={mainRef}
        in={isOpen}
        timeout={{ enter: 500, exit: 60 }}
      >
        {(state: any) => (
          <VStack
            ref={mainRef}
            alignItems="stretch"
            as="aside"
            transition="all ease 400ms"
            fontSize="14px"
            lineHeight="20px"
            gap="24px"
            paddingTop="16px"
            position="relative"
            style={{
              ...sideNavTransition[state],
            }}
            {...props}
            bg="sidebar.bg"
            backgroundImage="none"
          >
            <Flex
              justify="space-between"
              alignItems="center"
              minWidth="76px"
              transition="all 250ms ease 400ms"
              paddingX={renderDelay ? '16px' : '8px'}
            >
              <Logo isExpanded={isOpen!} />
              <ExpandButton
                isExpanded={isOpen!}
                onExpandClick={() => toggleExpand()}
              />
            </Flex>
            <Box
              flex="1"
              overflowY="scroll"
              css={{
                transition: `width 20ms ${cubicBezierFunction}`,
              }}
            >
              <SimpleBar
                style={{
                  flex: 1,
                  height: '100%',
                  padding: `0 ${renderDelay ? '16px' : '8px'}`,
                }}
              >
                <Transition
                  nodeRef={subRef}
                  in={renderDelay}
                  timeout={{ enter: 0, exit: 800 }}
                >
                  <Box
                    ref={subRef}
                    style={{
                      ...defaultStyle,
                      ...childTransition[state],
                    }}
                  >
                    <VStack gap="24px">
                      <HomeButton renderDelay={renderDelay} />
                      {menus
                        .filter(({ roles }) => hasRole(roles as Role[]))
                        .map((menu, ii) => (
                          <Box
                            /** Duplicate titles in helo 1, so nothing unique to base keys on. Remove this index based key hack after Helo 1 is removed. */
                            key={menu.title ? menu.title + ii : ii}
                            backgroundColor={menu.colors.menuBgColor}
                            borderRadius={renderDelay ? '14px' : '28px'}
                            padding={renderDelay ? '14px 10px' : '8px'}
                            width="100%"
                          >
                            {renderDelay ? (
                              <>
                                <Button
                                  variant="tertiary"
                                  width="100%"
                                  padding="6px"
                                  paddingLeft="20px"
                                  color={menu.colors.menuHeaderColor}
                                  onClick={() =>
                                    setIsExpanded(prev =>
                                      prev.map((v, idx) =>
                                        idx === ii ? !v : v,
                                      ),
                                    )
                                  }
                                  justifyContent="space-between"
                                  alignItems="center"
                                  fontWeight="700"
                                  borderRadius="16px"
                                  _hover={{
                                    bg: '#FFFFFF29',
                                    color: 'presets.neutral.0',
                                    svg: {
                                      color: 'presets.turquoise.100',
                                      borderColor: 'presets.neutral.0',
                                      bg: 'presets.neutral.0',
                                    },
                                  }}
                                  transition="all 150ms ease"
                                >
                                  {menu.title}
                                  <Icon
                                    icon="SingleChevronCircleIcon"
                                    color={menu.colors.menuHeaderColor}
                                    boxSize="16px"
                                    border="1px solid"
                                    borderRadius="8px"
                                    transition="transform 150ms ease"
                                    transform={`rotate(${isExpanded[ii] ? '0deg' : '180deg'})`}
                                  />
                                </Button>
                                <Collapse in={isExpanded[ii]} animateOpacity>
                                  {menu.items
                                    .filter(item =>
                                      hasRole(item.roles as Role[]),
                                    )
                                    .map((item, index) => {
                                      if ('isSubheader' in item)
                                        return (
                                          <MenuSubheader key={item.title + ii}>
                                            {item.title}
                                          </MenuSubheader>
                                        )

                                      return (
                                        <SidenavTooltip
                                          label={
                                            item.isDisabled
                                              ? item.tooltip || item.title
                                              : ''
                                          }
                                          marginLeft="28px"
                                          key={item.title + index}
                                        >
                                          <SidenavButton
                                            to={item.route}
                                            leftIcon={
                                              <Flex
                                                w="20px"
                                                justifyContent="center"
                                              >
                                                <Icon icon={item.icon as any} />
                                              </Flex>
                                            }
                                            isActive={pathname === item.route}
                                            isDisabled={!!item.isDisabled}
                                            backgroundColor={
                                              pathname === item.route
                                                ? menu.colors.activeBgColor
                                                : 'transparent'
                                            }
                                            color={
                                              pathname === item.route
                                                ? (menu.colors.activeColor ??
                                                  'presets.neutral.0')
                                                : (menu.colors.itemColor ??
                                                  'presets.neutral.0')
                                            }
                                            _active={{
                                              backgroundColor:
                                                menu.colors.activeBgColor,
                                              color:
                                                menu.colors.activeColor ??
                                                'presets.neutral.0',
                                            }}
                                            mb="1px"
                                          >
                                            {item.title}
                                          </SidenavButton>
                                        </SidenavTooltip>
                                      )
                                    })}
                                </Collapse>
                              </>
                            ) : (
                              menu.items
                                .filter(
                                  item =>
                                    !('isSubheader' in item) &&
                                    hasRole(item.roles as Role[]),
                                )
                                .map(
                                  item =>
                                    !('isSubheader' in item) && (
                                      <SidenavTooltip
                                        label={item.tooltip || item.title}
                                        marginLeft="18px"
                                        key={item.title + ii}
                                      >
                                        <SidenavButton
                                          as={Link}
                                          aria-label={item.title}
                                          to={item.route}
                                          isActive={pathname === item.route}
                                          isDisabled={!!item.isDisabled}
                                          backgroundColor={
                                            pathname === item.route
                                              ? menu.colors.activeBgColor
                                              : 'transparent'
                                          }
                                          color={
                                            pathname === item.route
                                              ? (menu.colors.activeColor ??
                                                'presets.neutral.0')
                                              : (menu.colors.itemColor ??
                                                'presets.neutral.0')
                                          }
                                          _active={{
                                            backgroundColor:
                                              menu.colors.activeBgColor,
                                          }}
                                          px="11px"
                                        >
                                          <Icon
                                            icon={item.icon as any}
                                            boxSize="16px"
                                          />
                                        </SidenavButton>
                                      </SidenavTooltip>
                                    ),
                                )
                            )}
                          </Box>
                        ))}
                    </VStack>
                  </Box>
                </Transition>
              </SimpleBar>
            </Box>
          </VStack>
        )}
      </Transition>
    </>
  )
}
